'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Asset } from './AssetItem';
import AssetItem from './AssetItem';
import AssetDetails from './AssetDetails';
import TaggedListView from './TaggedListView';
import UploadAssetDialog from './UploadAssetDialog';
import AssetPreviewModal from './AssetPreviewModal';
import AssetModifyModal from './AssetModifyModal';
import { assetsApi, AssetDto as ApiAsset } from '@/app/api/assets';
import { calculateMD5, generateMd5FromUrl } from '@/lib/assetTypeDetection';
import { toast } from 'react-hot-toast';
import TaggedSelect from '@/components/common/TaggedSelect';
import ConfirmDialog from '@/components/common/ConfirmDialog';

export default function AssetsManagement() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'image' | 'document' | 'video' | 'audio'>('all');
  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'image', label: 'Images' },
    { value: 'document', label: 'Documents' },
    { value: 'video', label: 'Videos' },
    { value: 'audio', label: 'Audio' },
  ];
  
  // Source type filter
  const [filterSourceType, setFilterSourceType] = useState<'all' | 'file' | 'url'>('all');
  const sourceTypeOptions = [
    { value: 'all', label: 'All Sources' },
    { value: 'file', label: 'File' },
    { value: 'url', label: 'URL' },
  ];
  
  const [isEditing, setIsEditing] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [previewMediaUrl, setPreviewMediaUrl] = useState<string | null>(null);
  
  // Search and filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const mapApiAssetToState = (apiAsset: ApiAsset): Asset => {
    let assetType = apiAsset.meta.assetType || '';
    const contentType = apiAsset.meta.contentType || '';
          
    // Validate the assetType is one of our valid types
    const validTypes = ['image', 'document', 'video', 'audio'];
    if (!validTypes.includes(assetType)) {
      // If assetType is not valid, try to determine it from contentType
      if (contentType.startsWith('image/')) {
        assetType = 'image';
      } else if (contentType.startsWith('video/')) {
        assetType = 'video';
      } else if (contentType.startsWith('audio/')) {
        assetType = 'audio';
      } else {
        assetType = 'document';
      }
    }
    
    // For URL assets, do additional checks if type is still not determined correctly
    if (apiAsset.type === 'url') {
      const url = apiAsset.meta.url || '';
      // Check for common video platforms in URL
      if (assetType !== 'video' && (url.includes('youtube.com') || url.includes('youtu.be') || 
          url.includes('vimeo.com') || url.includes('video'))) {
        assetType = 'video';
      }
    }

    return {
      id: apiAsset.uuid,
      uuid: apiAsset.uuid,
      md5: apiAsset.md5 || '',
      name: apiAsset.meta.filename || 'Untitled',
      contentType: assetType as 'image' | 'document' | 'video' | 'audio',
      size: formatFileSize(apiAsset.meta.size || 0),
      uploadedBy: 'You', // Could be enhanced with user info
      uploadDate: new Date(apiAsset.createdAt).toLocaleDateString(),
      description: apiAsset.meta.description || '',
      tags: (apiAsset.meta.tags || []).map((tag: any) => (typeof tag === 'string' ? { id: tag, name: tag } : tag)),
      url: assetsApi.getAssetContentUrl(apiAsset.uuid),
      originalAsset: apiAsset,
      sourceType: apiAsset.type === 'url' ? 'url' : 'file' // Add source type (file or url)
    };
  }


  const reloadAssets = useCallback(async () => {
    setIsLoading(true);
    try {
      const [apiAssets, tags] = await Promise.all([
        assetsApi.getAll(),
        assetsApi.getAllTags('assets'),
      ]);

      const formattedAssets: Asset[] = apiAssets.map(mapApiAssetToState);
      setAssets(formattedAssets);
      setAvailableTags(Array.isArray(tags) ? tags : []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadAssets();
  }, [reloadAssets]);
  
  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  // Filter assets based on selected tags and search query
  useEffect(() => {
    let result = [...assets];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(asset => 
        asset.name.toLowerCase().includes(query) ||
        (asset.description && asset.description.toLowerCase().includes(query)) ||
        getAssetTags(asset).some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Apply content type filtering
    if (filterType !== 'all') {
      result = result.filter(asset => asset.contentType === filterType);
    }
    
    // Apply source type filtering
    if (filterSourceType !== 'all') {
      result = result.filter(asset => asset.sourceType === filterSourceType);
    }
    
    // Apply tag filtering
    if (selectedTags.length > 0) {
      result = result.filter(asset => 
        asset.tags && asset.tags.some(tag => selectedTags.includes(tag.name))
      );
    }
    
    setFilteredAssets(result);
  }, [assets, searchQuery, filterType, filterSourceType, selectedTags]);

  // Handle asset selection
  const handleAssetSelect = (assetToSelect: Asset) => {
    setSelectedAsset(assetToSelect);
    setIsEditing(false);
  };
  
  // Handle tag selection for filtering
  const handleFilterByTag = (tag: string) => {
    // If tag is already selected, remove it
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      // Otherwise, add it to the selection
      setSelectedTags([...selectedTags, tag]);
    }
  };
  
  // Ensure we have a safe access to asset.tags
  const getAssetTags = (asset: Asset): string[] => {
    return asset.tags ? asset.tags.map(tag => tag.name) : [];
  };

  // Handle tag removal from filter
  const handleRemoveTagFilter = (tagToRemove: string) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  // Handle upload dialog
  const handleOpenUploadDialog = () => {
    setShowUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setShowUploadDialog(false);
  };

  // Handle asset upload
  const handleUploadAsset = async (currentAsset: Partial<Asset>, newFile?: File) => {
    setIsLoading(true);
    try {
      let response: ApiAsset;

      if (currentAsset.sourceType === 'file' && newFile) {
        const verification = await assetsApi.requestUpload(newFile);

        if (verification.isDuplicate) {
          toast.error(`Duplicate file detected: ${verification.duplicateInfo?.filename}`);
          throw new Error('Duplicate file');
        }

        if (!verification.r2Key) {
          throw new Error('Verification failed to provide an R2 key.');
        }

        if (!verification.uploadUrl) {
          throw new Error('Verification did not provide an upload URL.');
        }

        // Step 2: Upload the file directly to R2 using the presigned URL
        // Reuse the MD5 already calculated in requestUpload and convert to base64 format (same as backend)
        const hexPairs = verification.md5.match(/\w{2}/g);
        if (!hexPairs) {
          throw new Error('Invalid MD5 hash format');
        }
        const md5Base64 = btoa(hexPairs.map(byte => String.fromCharCode(parseInt(byte, 16))).join(''));
        
        const uploadResponse = await fetch(verification.uploadUrl, {
          method: 'PUT',
          body: newFile,
          headers: {
            'Content-Type': newFile.type,
            'Content-Length': newFile.size.toString(),
            'Content-MD5': md5Base64,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`File upload to R2 failed with status: ${uploadResponse.statusText}`);
        }

        // Step 3: Finalize the asset creation on our backend
        const metadataToCreate = {
          filename: newFile.name,
          description: currentAsset.description || '',
          tags: currentAsset.tags ? currentAsset.tags.map(tag => tag.name) : [],
          assetType: currentAsset.contentType || 'file',
          contentType: newFile.type,
          size: newFile.size,
        };

        console.log('Creating asset with metadata:', metadataToCreate);

        response = await assetsApi.createAsset(
          verification.uuid,
          verification.md5,
          verification.r2Key,
          metadataToCreate
        );

      } else if (currentAsset.sourceType === 'url' && currentAsset.url) {
        const verification = await assetsApi.checkUrlDuplicate(currentAsset.url);

        response = await assetsApi.createAsset(
          verification.uuid,
          verification.md5,
          '', // No R2 key for URLs
          {
            url: currentAsset.url,
            filename: currentAsset.name || verification.filename || 'URL Asset',
            description: currentAsset.description || '',
            tags: currentAsset.tags ? currentAsset.tags.map(tag => tag.name) : [],
            assetType: currentAsset.contentType || 'document',
            contentType: verification.contentType,
            size: verification.size,
          },
          'url'
        );
      } else {
        throw new Error('Invalid asset type or missing file/url');
      }

      if (!response || !response.uuid) {
        throw new Error('Invalid response from server');
      }

      const newAsset = mapApiAssetToState(response);
      
      setAssets(prevAssets => [newAsset, ...prevAssets]);
      setShowUploadDialog(false);
      toast.success('Asset uploaded successfully');

    } catch (error: any) {
  console.error('Failed to upload asset:', error);
  if (error.message !== 'Duplicate file' && error.message !== 'Duplicate URL') {
    toast.error('Failed to upload asset: ' + (error.message || 'Unknown error'));
  }
  // Re-throw the error so the promise is rejected and the caller can handle it
  throw error;
} finally {
  setIsLoading(false);
}

};

const handleAddTagToSelectedAsset = async (tags: string[]) => {
    if (!selectedAsset) return;

    const newTags = tags.map(tag => ({ id: `new_${tag}`, name: tag }));

    const optimisticAsset: Asset = {
      ...selectedAsset,
      tags: [...selectedAsset.tags, ...newTags]
    };
    setSelectedAsset(optimisticAsset);

    try {
      await assetsApi.addTagToAsset(selectedAsset.id, tags);
      await reloadAssets();
      toast.success('Tags added successfully');
    } catch (error) {
      console.error('Failed to add tags:', error);
      // Revert optimistic update
      setSelectedAsset(selectedAsset);
      toast.error('Failed to add tags. Please try again.');
    }
  };

  const handleRemoveTagFromSelectedAsset = async (tagToRemove: string) => {
    if (!selectedAsset || !selectedAsset.tags) return;
    
    try {
      // Call API to remove tag
      await assetsApi.removeTagFromAsset(selectedAsset.id, tagToRemove);
      
      const updatedAsset = { 
        ...selectedAsset,
        tags: selectedAsset.tags.filter(tag => tag.name !== tagToRemove)
      };
      
      // Update the asset in the assets array
      setAssets(prevAssets => 
        prevAssets.map(asset => 
          asset.id === selectedAsset.id ? updatedAsset : asset
        )
      );
      
      // Update the selected asset
      setSelectedAsset(updatedAsset);
      toast.success(`Tag "${tagToRemove}" removed`);
    } catch (error) {
      console.error('Failed to remove tag:', error);
      toast.error('Failed to remove tag');
    }
  };

  // Handle asset editing
  const handleStartEditing = () => {
    setShowEditDialog(true);
  };

  const handleSaveEdits = async (updatedAsset: Partial<Asset>) => {
    if (!selectedAsset) return;
    
    try {
      // Update the asset metadata via API
      const updatedMeta = {
        filename: updatedAsset.name,
        description: updatedAsset.description,
        tags: updatedAsset.tags ? updatedAsset.tags.map(tag => tag.name) : [],
        // Preserve other metadata
        ...(selectedAsset.originalAsset?.meta || {})
      };
      
      await assetsApi.updateAsset(selectedAsset.id!, updatedMeta);
      
      // Update the local asset state
      const updatedAssets = assets.map(asset => {
        if (asset.id === selectedAsset.id) {
          return {
            ...asset,
            name: updatedAsset.name || asset.name,
            description: updatedAsset.description || asset.description,
            tags: updatedAsset.tags || asset.tags,
            // Update the original asset metadata too
            originalAsset: {
              ...asset.originalAsset!,
              meta: updatedMeta
            }
          };
        }
        return asset;
      });
      
      setAssets(updatedAssets);
      setSelectedAsset(updatedAssets.find(a => a.id === selectedAsset.id) || null);
      setShowEditDialog(false);
      toast.success('Asset updated successfully');
    } catch (error) {
      console.error('Failed to update asset:', error);
      toast.error('Failed to update asset');
    }
  };

  const handleCancelEditing = () => {
    setShowEditDialog(false);
  };

  // Handle asset deletion
  const handleDeleteAsset = (assetId: string) => {
    setAssetToDelete(assetId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAsset = async () => {
    if (!assetToDelete) return;
    
    try {
      // Call API to delete the asset
      await assetsApi.deleteAsset(assetToDelete);
      
      // Remove the asset from the assets array
      setAssets(prevAssets => 
        prevAssets.filter(asset => asset.id !== assetToDelete)
      );
      
      // Clear the selected asset if it was deleted
      if (selectedAsset && selectedAsset.id === assetToDelete) {
        setSelectedAsset(null);
      }
      
      toast.success('Asset deleted successfully');
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast.error('Failed to delete asset');
    } finally {
      setShowDeleteConfirm(false);
      setAssetToDelete(null);
    }
  };

  // Handle asset preview
  const handlePreview = async () => {
    console.log('handlePreview called', { selectedAsset });
    if (!selectedAsset) {
      console.log('No selected asset');
      return;
    }
    
    try {
      // Load media URL for preview
      if (selectedAsset.contentType === 'image' || selectedAsset.contentType === 'video') {
        console.log('Loading preview for:', selectedAsset.contentType);
        let url: string;
        if (selectedAsset.sourceType === 'url' && selectedAsset.originalAsset?.meta?.url) {
          url = selectedAsset.originalAsset.meta.url;
          console.log('Using URL asset:', url);
        } else {
          console.log('Loading authenticated blob URL for:', selectedAsset.id);
          const { createAuthenticatedBlobUrl } = await import('@/app/api/proxy');
          url = await createAuthenticatedBlobUrl(selectedAsset.id);
          console.log('Got blob URL:', url);
        }
        setPreviewMediaUrl(url);
        setShowPreviewModal(true);
        console.log('Preview modal should be visible now');
      } else {
        console.log('Asset type not supported for preview:', selectedAsset.contentType);
      }
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load preview');
    }
  };

  // Handle opening modify modal from preview
  const handleOpenModify = () => {
    setShowPreviewModal(false);
    setShowModifyModal(true);
  };

  // Handle saving modified image
  const handleSaveModifiedImage = async (croppedImageFile: File) => {
    if (!selectedAsset) return;
    
    try {
      // Update the asset content with the new cropped image
      const updatedAsset = await assetsApi.updateAssetContent(selectedAsset.id, croppedImageFile);
      
      // Update local state
      const newAsset = mapApiAssetToState(updatedAsset);
      setAssets(prevAssets => 
        prevAssets.map(asset => 
          asset.id === selectedAsset.id ? newAsset : asset
        )
      );
      setSelectedAsset(newAsset);
      
      // Refresh the cached preview image to show the updated content
      if (previewMediaUrl) {
        // Clear the cache and reload the updated image
        try {
          const { revokeBlobUrl, createAuthenticatedBlobUrl } = await import('@/app/api/proxy');
          
          // Revoke the cached blob URL for this asset
          revokeBlobUrl(selectedAsset.id);
          
          // Fetch the updated image with cache bypass to force fresh content
          const newUrl = await createAuthenticatedBlobUrl(selectedAsset.id, true);
          setPreviewMediaUrl(newUrl);
        } catch (error) {
          console.error('Failed to refresh preview:', error);
          // Don't show error toast, the save was successful
        }
      }
      
      toast.success('Image updated successfully');
      setShowModifyModal(false);
    } catch (error) {
      console.error('Failed to update image:', error);
      toast.error('Failed to update image');
      throw error;
    }
  };

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Assets Management</h1>
          <button
            onClick={handleOpenUploadDialog}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Upload Asset
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar - Asset list */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Filter Assets</div>
                <div className="flex space-x-4">
                  <div className="w-1/2">
                    <TaggedSelect
                      value={filterType}
                      onChange={(value) => setFilterType(value as 'all' | 'image' | 'document' | 'video' | 'audio')}
                      options={typeOptions}
                      placeholder="Select category"
                      className="w-full"
                    />
                  </div>
                  <div className="w-1/2">
                    <TaggedSelect
                      value={filterSourceType}
                      onChange={(value) => setFilterSourceType(value as 'all' | 'file' | 'url')}
                      options={sourceTypeOptions}
                      placeholder="Select source"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Search by information</div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search assets..."
                  />
                  {searchQuery && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button 
                        onClick={() => setSearchQuery('')}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Tag filter section */}
              {!isLoading && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">Filter by Tags</div>
                  <TaggedSelect
                    value={selectedTags}
                    onChange={(value) => setSelectedTags(value as string[])}
                    options={Array.isArray(availableTags) ? availableTags.map(tag => ({ value: tag, label: tag })) : []}
                    placeholder="Add a tag filter..."
                    className="w-full"
                    multiple={true}
                    editable={true}
                    onCreateOption={(label) => {
                      if (Array.isArray(availableTags) && !availableTags.includes(label)) {
                        setAvailableTags(prev => [...prev, label]);
                      }
                      if (!selectedTags.includes(label)) {
                        setSelectedTags(prev => [...prev, label]);
                      }
                    }}
                  />
                </div>
              )}
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
              </div>
            ) : (
              <TaggedListView
                items={filteredAssets}
                onItemSelect={handleAssetSelect}
                selectedItem={selectedAsset}
                renderItemContent={(asset: Asset) => (
                  <AssetItem 
                    asset={asset} 
                    onTagClick={handleFilterByTag} 
                  />
                )}
                emptyMessage="No assets found. Try adjusting your filters or upload a new asset."
                className="h-[calc(100vh-300px)]"
              />
            )}
          </div>

          {/* Main content - Asset details */}
          <div className="lg:col-span-2">
            <AssetDetails
              asset={selectedAsset}
              isEditing={isEditing}
              onStartEditing={handleStartEditing}
              onSaveEdits={handleSaveEdits}
              onCancelEditing={handleCancelEditing}
              onDeleteAsset={handleDeleteAsset}
              onAddTag={handleAddTagToSelectedAsset}
              onRemoveTag={handleRemoveTagFromSelectedAsset}
              availableTags={Array.isArray(availableTags) ? availableTags.map(tag => ({ value: tag, label: tag })) : []}
              onPreview={handlePreview}
            />
          </div>
        </div>

        {/* Upload dialog */}
        {showUploadDialog && (
          <UploadAssetDialog
            isOpen={showUploadDialog}
            onClose={handleCloseUploadDialog}
            onUpload={(assetData) => handleUploadAsset(assetData, assetData.file as File | undefined)}
          />
        )}
        
        {/* Edit dialog */}
        {showEditDialog && selectedAsset && (
          <UploadAssetDialog
            isOpen={showEditDialog}
            onClose={handleCancelEditing}
            onUpload={handleSaveEdits}
            editMode={true}
            assetToEdit={selectedAsset}
          />
        )}

        {/* Delete confirmation dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setAssetToDelete(null);
          }}
          onConfirm={confirmDeleteAsset}
          title="Delete Asset"
          message="Are you sure you want to delete this asset? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClassName="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
        />

        {/* Asset Preview Modal */}
        <AssetPreviewModal
          asset={selectedAsset}
          mediaUrl={previewMediaUrl}
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          onModify={handleOpenModify}
        />

        {/* Asset Modify Modal */}
        <AssetModifyModal
          asset={selectedAsset}
          mediaUrl={previewMediaUrl}
          isOpen={showModifyModal}
          onClose={() => setShowModifyModal(false)}
          onSave={handleSaveModifiedImage}
        />
      </div>
    </div>
  );
}
